import { mapFromDashboard } from 'mappings/dashboardMapping';
import { ProjectRoomKey } from 'models/projects/projectEnums';
import {
  mapFromKickstartOffer,
  mapFromProjectApplyResult,
  mapFromProject,
  mapFromProjects,
  mapFromProjectRoom,
  mapFromProjectTrack,
  mapToProjectStateRequest,
  mapToProjectTrackPreviewRequest,
} from 'mappings/projectMapping';
import {
  KickstartOfferDTO,
  ProjectDTO,
  ProjectStateRequestDTO,
  ProjectSummaryDTO,
  ProjectTrackDTO,
} from 'models/dto/projects/projectDTO';
import {
  ProjectApplyRequestDTO,
  ProjectApplyResultDTO,
  ProjectRoomDTO,
} from 'models/dto/projects/projectRoomDTO';
import { ProjectReopenRequestDTO } from 'models/dto/projects/projectReopenDTO';
import { ProjectJourneyUpdateDTO } from 'models/dto/projects/projectJourneyDTO';
import { mapFromProjectJourneyUpdate } from 'mappings/projectJourneyMapping';
import { ProjectJourneyUpdate } from 'models/projects/projectJourney';
import Project, {
  KickstartOffer,
  ProjectSummary,
  ProjectTrack,
} from 'models/projects/project';
import ProjectRoom, { ProjectApplyResult } from 'models/projects/projectRoom';
import Dashboard from 'models/dashboard';
import DashboardDTO from 'models/dto/dashboardDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import AppError from 'utils/appError';
import { toApiDateTimeOptional } from 'utils/datetime';
import http from './httpService';

/**
 * Returns the project summaries: no milestones, rooms, tier or track.
 * Use `apiGetProject` to load those.
 */
export async function apiGetProjects(accessToken: string): Promise<ProjectSummary[]> {
  try {
    const ar = await http.get<ProjectSummaryDTO[]>('/projects', http.getRequestConfig(accessToken));

    return mapFromProjects(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/** The Kickstart package in the organization's Store and its trial, found by the server on the package code. */
export async function apiGetKickstartOffer(accessToken: string): Promise<KickstartOffer> {
  try {
    const ar = await http.get<KickstartOfferDTO>(`/projects/kickstart/offer`, http.getRequestConfig(accessToken));

    return mapFromKickstartOffer(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Returns project details with `hasDetail === true`, or undefined for an absent project.
 * Other request failures propagate as `AppError`.
 */
export async function apiGetProject(
  projectId: number,
  accessToken: string,
  cache?: GlobalDataCache,
): Promise<Project | undefined> {
  try {
    const ar = await http.get<ProjectDTO>(`/projects/${projectId}`, http.getRequestConfig(accessToken));
    if (ar.status === 204 || !ar.data) return undefined;

    return mapFromProject(ar.data, true, cache);
  } catch (err) {
    const appErr = AppError.fromApiError(err);
    if (appErr.code === '404') return undefined;

    throw appErr;
  }
}

/**
 * Changes the project state. A mismatched `expectedState` returns `PROJECT0001` in an `AppError`.
 * The response excludes project details and has `hasDetail === false`.
 */
export async function apiSetProjectState(
  projectId: number,
  request: ProjectStateRequestDTO,
  accessToken: string,
): Promise<Project> {
  try {
    const ar = await http.put<ProjectDTO>(`/projects/${projectId}`, request, http.getRequestConfig(accessToken));

    return mapFromProject(ar.data, false);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Updates the target date without changing the project state; undefined clears the date.
 * A mismatched `expectedState` returns `PROJECT0001`.
 * The response has `hasDetail === false`; reload project details to refresh the timeline.
 */
export async function apiSetProjectTargetDate(
  projectId: number,
  targetDate: Date | undefined,
  expectedState: number | undefined,
  accessToken: string,
): Promise<Project> {
  try {
    const request = mapToProjectStateRequest(undefined, expectedState, targetDate, targetDate === undefined);
    const ar = await http.put<ProjectDTO>(`/projects/${projectId}`, request, http.getRequestConfig(accessToken));

    return mapFromProject(ar.data, false);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * The forecast as it would stand against `targetDate`, without writing. Undefined `targetDate`
 * previews a project with no target.
 */
export async function apiPreviewProjectTrack(
  projectId: number,
  targetDate: Date | undefined,
  accessToken: string,
): Promise<ProjectTrack> {
  try {
    const request = mapToProjectTrackPreviewRequest(targetDate, targetDate === undefined);
    const ar = await http.post<ProjectTrackDTO>(
      `/projects/${projectId}/track`,
      request,
      http.getRequestConfig(accessToken),
    );

    return mapFromProjectTrack(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Completes a journey task after validating its checklist and updates milestone completion.
 * Requires manager access. Generic task updates do not perform these project-specific checks.
 */
export async function apiCompleteProjectTask(projectId: number, taskId: number, accessToken: string): Promise<void> {
  try {
    await http.post(`/projects/${projectId}/tasks/${taskId}/complete`, {}, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Switches a milestone: closes it and moves the journey on to the next open milestone. No milestone
 * closes by itself. Requires manager access. A task that is not a milestone of this project returns
 * `IP0002`; a milestone that is already closed returns `PROJECT0061`; an open milestone that is not
 * the current one (the first open milestone) returns `PROJECT0060`.
 */
export async function apiSwitchProjectMilestone(projectId: number, taskId: number, accessToken: string): Promise<void> {
  try {
    await http.post(`/projects/${projectId}/milestones/${taskId}/switch`, {}, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Reopens a step or deliverable and its milestone; an open task requires no change.
 * A changed completion stamp returns `PROJECT0001`; a failed reopen returns `PROJECT0021`.
 */
export async function apiReopenProjectTask(
  projectId: number,
  taskId: number,
  expectedCompleted: Date | undefined,
  accessToken: string,
): Promise<void> {
  try {
    const body = new ProjectReopenRequestDTO();
    body.expectedCompleted = toApiDateTimeOptional(expectedCompleted);
    await http.post(`/projects/${projectId}/tasks/${taskId}/reopen`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Deletes the project and returns false when it was already absent (HTTP 204).
 */
export async function apiDeleteProject(projectId: number, accessToken: string): Promise<boolean> {
  try {
    const ar = await http.delete(`/projects/${projectId}`, http.getRequestConfig(accessToken));

    return ar.status !== 204;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * What updating this project's journey would change: against the newest newer version of its
 * package when there is one, else against its own package. Nothing is written; updating itself is
 * installing that version through the Store.
 */
export async function apiGetProjectJourneyUpdate(
  projectId: number,
  accessToken: string,
): Promise<ProjectJourneyUpdate> {
  try {
    const ar = await http.get<ProjectJourneyUpdateDTO>(
      `/projects/${projectId}/journey/update`,
      http.getRequestConfig(accessToken),
    );

    return mapFromProjectJourneyUpdate(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// Room routes use `roomKey`. Callers handle project errors from `AppError.data.code`.

/**
 * The one list a read of an answers room (asset-answers, outcome-answers) is narrowed to: the list a role without a task
 * would get (read-only, for whoever manages the inquiries). An inquiry task's list is read by its task
 * (apiGetProjectInquiryTaskRoom).
 */
export type ProjectRoomListScope = { roleId: string };

/**
 * Returns room items and the deliverable task; of an answers room only the list `list` names, when given.
 * Propagates HTTP 404 so the frame distinguishes an absent room from an empty room; a role that already has its task, or
 * is no row of its send room, is a 404 too.
 */
export async function apiGetProjectRoom(
  roomKey: ProjectRoomKey,
  projectId: number,
  accessToken: string,
  list?: ProjectRoomListScope,
): Promise<ProjectRoom> {
  const query: string = list === undefined ? '' : `?roleId=${encodeURIComponent(list.roleId)}`;
  try {
    const ar = await http.get<ProjectRoomDTO>(
      `/projects/${projectId}/rooms/${encodeURIComponent(roomKey)}${query}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromProjectRoom(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Returns the answers room of an inquiry task with that task's list only: what the inquiry task window shows. The room
 * and its project (`projectId`, for the apply) follow from the task. A task that is no inquiry task is a 404, which
 * propagates as `AppError`.
 */
export async function apiGetProjectInquiryTaskRoom(taskId: number, accessToken: string): Promise<ProjectRoom> {
  try {
    const ar = await http.get<ProjectRoomDTO>(`/projects/inquiry-tasks/${taskId}/room`, http.getRequestConfig(accessToken));

    return mapFromProjectRoom(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Saves item decisions and optionally confirms the deliverable, returning per-item outcomes.
 * Retries submit failed decisions again. Project errors propagate as `AppError`.
 */
export async function apiPostProjectApply(
  projectId: number,
  roomKey: ProjectRoomKey,
  request: ProjectApplyRequestDTO,
  accessToken: string,
): Promise<ProjectApplyResult> {
  // `action` goes first in every decision: the API reads a decision's type from its first member only. A decision class
  // sets its action after the base class sets `itemId`, so each decision is copied onto an object that starts with it.
  const body: ProjectApplyRequestDTO = {
    ...request,
    decisions: request.decisions.map((decision) => Object.assign({ action: decision.action }, decision)),
  };
  try {
    const ar = await http.post<ProjectApplyResultDTO>(
      `/projects/${projectId}/rooms/${encodeURIComponent(roomKey)}/apply`,
      body,
      http.getRequestConfig(accessToken),
    );

    return mapFromProjectApplyResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCreateProjectDashboard(
  projectId: number,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Dashboard> {
  try {
    const ar = await http.post<DashboardDTO>(
      `/projects/${projectId}/dashboard`,
      null,
      http.getRequestConfig(accessToken),
    );

    return mapFromDashboard(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
